import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  Connection,
  Node,
  Edge,
  MarkerType,
  OnSelectionChangeParams,
  ConnectionMode,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./automationFlow.css";
import YhButton from "../../ui/YhButton";
import YhInput from "../../ui/YhInput";
import YhSelect from "../../ui/YhSelect";
import {
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import notifiers from "../../global/constants/NotificationConstants";
import urls from "../../global/constants/UrlConstants";
import { selectActiveOrganizationId } from "../../redux/authSlice";
import {
  AutomationNode,
  AutomationNodeType,
  createAutomationWorkflow,
  getAutomationWorkflow,
  publishAutomationWorkflow,
  toIdString,
  updateAutomationWorkflow,
} from "../../services/automationService";
import { fetchAuthKeyTemplates, fetchAutomationRoutingOptions, upsertOrgLeadSource } from "../../services/organizationService";
import {
  BlockIcon,
  WORKFLOW_BLOCKS,
  WorkflowFlowNode,
  WorkflowNodeData,
  blockMeta,
} from "./workflowBlocks";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import { getStage } from "../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import { listFeedbackForms } from "../Lead/components/stepper/StepperService";

const ACTION_TYPE_OPTIONS = [
  { label: "Send WhatsApp", value: "send_whatsapp" },
  { label: "Send SMS", value: "send_sms" },
  { label: "Send Email", value: "send_email" },
  { label: "Feedback request", value: "feedback" },
  { label: "Update lead stage", value: "update_stage" },
  { label: "Assign (round robin)", value: "smart_routing" },
];

const WA_PURPOSE_OPTIONS = [
  { label: "Thank you", value: "thank_you" },
  { label: "Site visit reminder", value: "site_visit_reminder" },
  { label: "Site visit confirm", value: "site_visit_confirm" },
  { label: "Site visit completed", value: "site_visit_completed" },
  { label: "Calling done", value: "calling_done" },
  { label: "Custom", value: "custom" },
];

const SCHEDULE_MODE_OPTIONS = [
  { label: "Immediately", value: "immediate" },
  { label: "After delay (minutes)", value: "delay" },
  { label: "At a specific time", value: "at_time" },
  { label: "Between time window", value: "between" },
  { label: "Multiple times in a day", value: "multiple" },
];

function defaultSchedule(mode = "immediate") {
  return {
    mode,
    delayMinutes: 0,
    atTime: "10:00",
    betweenStart: "09:00",
    betweenEnd: "20:00",
    times: ["10:00", "16:00"],
    timezone: "Asia/Kolkata",
  };
}

function flattenLeadStages(tree: any[]): { id: string; label: string }[] {
  const result: { id: string; label: string }[] = [];
  const walk = (nodes: any[], prefix = "") => {
    (nodes || []).forEach((stage) => {
      const name = String(stage?.stage_Name || stage?.stage_name || "").trim();
      if (!name) return;
      const label = prefix ? `${prefix} › ${name}` : name;
      result.push({ id: name, label });
      if (Array.isArray(stage.sub_Stage_name) && stage.sub_Stage_name.length) {
        walk(stage.sub_Stage_name, name);
      }
    });
  };
  walk(tree);
  return result;
}

const nodeTypes = { workflow: WorkflowFlowNode };

const uid = (prefix: string) =>
  `${prefix}_${Math.random().toString(36).slice(2, 9)}`;

const selectValue = (e: any) =>
  typeof e === "string" ? e : e?.target?.value || "";

const toFlowNodes = (nodes: AutomationNode[]): Node[] =>
  (nodes || []).map((n) => ({
    id: n.id,
    type: "workflow",
    position: n.position || { x: 0, y: 0 },
    data: {
      ...(n.data || {}),
      type: (n.data?.type || n.type) as AutomationNodeType,
      label: n.data?.label || blockMeta(n.data?.type || n.type).label,
    },
    deletable: n.type !== "start" && n.type !== "end",
  }));

const toFlowEdges = (edges: any[]): Edge[] =>
  (edges || []).map((e) => ({
    id: e.id || uid("edge"),
    source: e.source,
    target: e.target,
    label: e.label || "",
    type: "smoothstep",
    animated: false,
    markerEnd: { type: MarkerType.ArrowClosed, color: "#64748B", width: 18, height: 18 },
    style: { stroke: "#94A3B8", strokeWidth: 1.75 },
    labelStyle: { fill: "#64748B", fontSize: 10, fontWeight: 600 },
    labelBgStyle: { fill: "#F8FAFC", fillOpacity: 0.95 },
    labelBgPadding: [4, 6] as [number, number],
    labelBgBorderRadius: 4,
  }));

const fromFlowNodes = (nodes: Node[]): AutomationNode[] =>
  nodes.map((n) => {
    const data = (n.data || {}) as WorkflowNodeData;
    const type = (data.type || "lead_capture") as AutomationNodeType;
    return {
      id: n.id,
      type,
      position: n.position,
      data: { ...data, type, label: data.label || blockMeta(type).label },
    };
  });

const fromFlowEdges = (edges: Edge[]) =>
  edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: typeof e.label === "string" ? e.label : "",
  }));

const defaultNewNodes = (): Node[] =>
  toFlowNodes([
    {
      id: "start",
      type: "start",
      position: { x: 80, y: 180 },
      data: { type: "start", label: "Start" },
    },
    {
      id: "end",
      type: "end",
      position: { x: 640, y: 180 },
      data: { type: "end", label: "End" },
    },
  ]);

const AutomationEditorInner: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ id?: string }>();
  const routeId = toIdString(params.id);
  const isNew = !routeId || routeId === "new" || routeId === "[object Object]";
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const { screenToFlowPosition, fitView, zoomIn, zoomOut } = useReactFlow();

  const [workflowId, setWorkflowId] = useState<string | null>(
    isNew ? null : routeId
  );
  const [name, setName] = useState("Untitled workflow");
  const [description, setDescription] = useState("");
  const [triggerType, setTriggerType] = useState("lead_capture");
  const [status, setStatus] = useState("draft");
  const [workflowScope, setWorkflowScope] = useState<
    "organization" | "global" | "project" | "form"
  >("organization");
  const [scopeProjectIds, setScopeProjectIds] = useState<string[]>([]);
  const [scopeFormIds, setScopeFormIds] = useState<string[]>([]);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState<
    { wid: string; name: string; label: string }[]
  >([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [feedbackForms, setFeedbackForms] = useState<
    {
      _id: string;
      form_name: string;
      publicUrl?: string;
      formScope?: string;
      projectSlug?: string;
      orgSlug?: string;
    }[]
  >([]);
  const [loadingFeedbackForms, setLoadingFeedbackForms] = useState(false);
  const [routingOptions, setRoutingOptions] = useState<{
    sources: { _id: string; source_name: string; sourceGroup: string }[];
    projects: {
      _id: string;
      projectId: string;
      projectName: string;
      city: string;
      location: string;
    }[];
    locations: string[];
    departments: { _id: string; name: string }[];
    teams: {
      teamId: string;
      teamName: string;
      departmentId?: string;
      departmentName?: string;
      teamRole?: string;
    }[];
    members: {
      userId: string;
      name: string;
      email: string;
      roleSlug: string;
      teamIds?: string[];
    }[];
  }>({
    sources: [],
    projects: [],
    locations: [],
    departments: [],
    teams: [],
    members: [],
  });
  const [newSourceName, setNewSourceName] = useState("");
  const [leadStages, setLeadStages] = useState<{ id: string; label: string }[]>(
    []
  );
  const fittedRef = useRef(false);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );
  const selectedEdge = useMemo(
    () => edges.find((e) => e.id === selectedEdgeId) || null,
    [edges, selectedEdgeId]
  );
  const selectedType = String(
    (selectedNode?.data as WorkflowNodeData)?.type || ""
  );

  useEffect(() => {
    if (isNew) {
      setNodes(defaultNewNodes());
      setEdges([]);
      setSelectedNodeId("start");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        fittedRef.current = false;
        const res = await getAutomationWorkflow(routeId);
        const doc = res?.data || res;
        setWorkflowId(toIdString(doc._id) || routeId);
        setName(doc.name || "Untitled workflow");
        setDescription(doc.description || "");
        setTriggerType(doc.triggerType || "lead_capture");
        setStatus(doc.status || "draft");
        const sc = String(
          doc.scope || doc.settings?.scope || "organization"
        ).toLowerCase();
        setWorkflowScope(
          ["global", "project", "form", "organization"].includes(sc)
            ? (sc as any)
            : "organization"
        );
        setScopeProjectIds(
          Array.isArray(doc.scopeProjectIds)
            ? doc.scopeProjectIds.map(String)
            : Array.isArray(doc.settings?.projectIds)
              ? doc.settings.projectIds.map(String)
              : []
        );
        setScopeFormIds(
          Array.isArray(doc.scopeFormIds)
            ? doc.scopeFormIds.map(String)
            : Array.isArray(doc.settings?.formIds)
              ? doc.settings.formIds.map(String)
              : []
        );
        setNodes(toFlowNodes(doc.nodes || []));
        setEdges(toFlowEdges(doc.edges || []));
      } catch (error: any) {
        openErrorNotification(
          isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [isNew, routeId, setNodes, setEdges]);

  useEffect(() => {
    if (loading || fittedRef.current || !nodes.length) return;
    const t = setTimeout(() => {
      fitView({ padding: 0.2, duration: 200 });
      fittedRef.current = true;
    }, 60);
    return () => clearTimeout(t);
  }, [loading, nodes.length, fitView]);

  useEffect(() => {
    const needsWa = [
      "send_whatsapp",
      "feedback",
      "stage_action",
      "multi_action",
    ].includes(selectedType);
    if (!needsWa) return;
    if (!activeOrganizationId || activeOrganizationId === "all") return;
    if (templates.length) return;
    loadWhatsAppTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, selectedType, activeOrganizationId]);

  useEffect(() => {
    const needsFeedback =
      selectedType === "feedback" ||
      (Array.isArray((selectedNode?.data as WorkflowNodeData)?.actions) &&
        ((selectedNode?.data as WorkflowNodeData).actions || []).some(
          (a: any) => a?.type === "feedback"
        ));
    if (!needsFeedback) return;
    if (!activeOrganizationId || activeOrganizationId === "all") return;
    if (feedbackForms.length) return;
    loadFeedbackForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId, selectedType, activeOrganizationId]);

  useEffect(() => {
    if (!activeOrganizationId || activeOrganizationId === "all") return;
    loadRoutingOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganizationId]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getStage();
        const tree = res?.data || res || [];
        setLeadStages(flattenLeadStages(Array.isArray(tree) ? tree : []));
      } catch (_) {
        setLeadStages([]);
      }
    })();
  }, []);

  const loadRoutingOptions = async () => {
    if (!activeOrganizationId || activeOrganizationId === "all") return;
    try {
      const res = await fetchAutomationRoutingOptions(activeOrganizationId);
      const data = res?.data || {};
      setRoutingOptions({
        sources: Array.isArray(data.sources) ? data.sources : [],
        projects: Array.isArray(data.projects) ? data.projects : [],
        locations: Array.isArray(data.locations) ? data.locations : [],
        departments: Array.isArray(data.departments) ? data.departments : [],
        teams: Array.isArray(data.teams) ? data.teams : [],
        members: Array.isArray(data.members) ? data.members : [],
      });
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load routing options");
    }
  };

  const loadWhatsAppTemplates = async () => {
    if (!activeOrganizationId || activeOrganizationId === "all") {
      openErrorNotification("Select an organization to load WhatsApp templates");
      return;
    }
    setLoadingTemplates(true);
    try {
      const res = await fetchAuthKeyTemplates(activeOrganizationId, "whatsapp");
      const raw = res?.data || [];
      const list = (Array.isArray(raw) ? raw : [])
        .map((t: any) => {
          const wid = String(t.wid || t.id || t.template_id || "");
          const tName = String(t.name || t.template_name || wid);
          if (!wid) return null;
          return { wid, name: tName, label: `${tName} (${wid})` };
        })
        .filter(Boolean) as { wid: string; name: string; label: string }[];
      setTemplates(list);
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const loadFeedbackForms = async () => {
    setLoadingFeedbackForms(true);
    try {
      const res = await listFeedbackForms();
      const raw = res?.data || [];
      setFeedbackForms(
        (Array.isArray(raw) ? raw : []).map((f: any) => ({
          _id: String(f._id),
          form_name: f.form_name || "Feedback form",
          publicUrl: f.publicUrl || "",
          formScope: f.formScope || "",
          projectSlug: f.projectSlug || "",
          orgSlug: f.orgSlug || "",
        }))
      );
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load feedback forms");
    } finally {
      setLoadingFeedbackForms(false);
    }
  };

  const renderFeedbackFormPicker = (
    value: string,
    onPick: (formId: string) => void
  ) => (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-[#374151]">Feedback form</p>
        <button
          type="button"
          className="text-xs font-semibold text-[#1E3A5F] hover:underline"
          onClick={loadFeedbackForms}
          disabled={loadingFeedbackForms}
        >
          {loadingFeedbackForms ? "Loading…" : "Refresh"}
        </button>
      </div>
      <YhSelect
        label="Form"
        value={value || ""}
        onChange={(e: any) => onPick(selectValue(e))}
        options={[
          {
            label: feedbackForms.length
              ? "Select feedback form"
              : "Create a feedback form first",
            value: "",
          },
          ...feedbackForms.map((f) => ({
            label: `${f.form_name}${
              (f as any).formScope === "project" || (f as any).projectSlug
                ? " · project"
                : " · org"
            }`,
            value: f._id,
          })),
        ]}
      />
      <div className="rounded-xl border border-[#F9F0F4] bg-[#F8FAFF] px-2.5 py-2 text-[11px] leading-relaxed text-[#475569]">
        <p className="font-semibold text-[#1E3A5F]">How dynamic links work</p>
        <ol className="mt-1 list-decimal space-y-0.5 pl-4">
          <li>Pick a feedback form (org/project scoped).</li>
          <li>Pick WhatsApp template with Visit website Dynamic {"{{1}}"}.</li>
          <li>
            Set template website base to public host (e.g.{" "}
            <code>localhost:3000</code>).
          </li>
          <li>
            On trigger, each lead gets{" "}
            <code>feedback/&#123;org&#125;/t/&#123;token&#125;</code> in{" "}
            {"{{1}}"}.
          </li>
        </ol>
      </div>
    </>
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;
      if (connection.source === connection.target) return;
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            id: uid("edge"),
            type: "smoothstep",
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: "#64748B",
              width: 18,
              height: 18,
            },
            style: { stroke: "#94A3B8", strokeWidth: 1.75 },
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const onSelectionChange = useCallback((params: OnSelectionChangeParams) => {
    const n = params.nodes?.[0];
    const e = params.edges?.[0];
    setSelectedNodeId(n?.id || null);
    setSelectedEdgeId(e?.id || null);
  }, []);

  const isValidConnection = useCallback((connection: Connection | Edge) => {
    if (!connection.source || !connection.target) return false;
    if (connection.source === connection.target) return false;
    return true;
  }, []);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData(
        "application/reactflow"
      ) as AutomationNodeType;
      if (!type) return;

      if (type === "start" && nodes.some((n) => (n.data as any)?.type === "start")) {
        openErrorNotification("Workflow already has a Start node");
        return;
      }
      if (type === "end" && nodes.some((n) => (n.data as any)?.type === "end")) {
        openErrorNotification("Workflow already has an End node");
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const meta = blockMeta(type);
      const id = type === "start" || type === "end" ? type : uid("node");
      const newNode: Node = {
        id,
        type: "workflow",
        position,
        data: {
          type,
          label: meta.label,
          ...(meta.defaults || {}),
        },
        deletable: type !== "start" && type !== "end",
      };
      setNodes((nds) => nds.concat(newNode));
      setSelectedNodeId(id);
      setSelectedEdgeId(null);
    },
    [nodes, screenToFlowPosition, setNodes]
  );

  const addBlock = (type: AutomationNodeType) => {
    if (type === "start" && nodes.some((n) => (n.data as any)?.type === "start")) {
      openErrorNotification("Workflow already has a Start node");
      return;
    }
    if (type === "end" && nodes.some((n) => (n.data as any)?.type === "end")) {
      openErrorNotification("Workflow already has an End node");
      return;
    }
    const meta = blockMeta(type);
    const id = type === "start" || type === "end" ? type : uid("node");
    const count = nodes.length;
    const newNode: Node = {
      id,
      type: "workflow",
      position: {
        x: 120 + (count % 4) * 240,
        y: 100 + Math.floor(count / 4) * 120,
      },
      data: {
        type,
        label: meta.label,
        ...(meta.defaults || {}),
      },
      deletable: type !== "start" && type !== "end",
    };
    setNodes((nds) => nds.concat(newNode));
    setSelectedNodeId(id);
  };

  const onPaletteDragStart = (
    event: React.DragEvent,
    type: AutomationNodeType
  ) => {
    event.dataTransfer.setData("application/reactflow", type);
    event.dataTransfer.effectAllowed = "move";
  };

  const updateSelectedData = (patch: Record<string, any>) => {
    if (!selectedNodeId) return;
    setNodes((nds) =>
      nds.map((n) =>
        n.id === selectedNodeId
          ? { ...n, data: { ...(n.data as object), ...patch } }
          : n
      )
    );
  };

  const getActions = (): any[] => {
    const raw = (selectedNode?.data as WorkflowNodeData)?.actions;
    return Array.isArray(raw) ? raw : [];
  };

  const setActions = (actions: any[]) => {
    updateSelectedData({ actions });
  };

  const addAction = (type = "send_whatsapp") => {
    const actions = getActions();
    setActions([
      ...actions,
      {
        id: uid("act"),
        type,
        actionKey: type === "send_whatsapp" ? "thank_you" : "",
        label:
          ACTION_TYPE_OPTIONS.find((o) => o.value === type)?.label || type,
        templateWid: "",
        templateName: "",
        targetStage: "",
      },
    ]);
  };

  const updateAction = (actionId: string, patch: Record<string, any>) => {
    setActions(
      getActions().map((a) => (a.id === actionId ? { ...a, ...patch } : a))
    );
  };

  const removeAction = (actionId: string) => {
    setActions(getActions().filter((a) => a.id !== actionId));
  };

  const renderScheduleFields = (
    schedule: any,
    onChange: (next: Record<string, any>) => void
  ) => {
    const s = { ...defaultSchedule(), ...(schedule || {}) };
    return (
      <div className="flex flex-col gap-2 rounded-md border border-[#E5E7EB] bg-white p-2">
        <YhSelect
          label="When to send"
          value={s.mode || "immediate"}
          onChange={(e: any) => {
            const mode = selectValue(e) || "immediate";
            onChange({ ...s, mode });
          }}
          options={SCHEDULE_MODE_OPTIONS}
        />
        {s.mode === "delay" && (
          <YhInput
            label="Delay (minutes)"
            type="number"
            value={String(s.delayMinutes ?? 0)}
            onChange={(e) =>
              onChange({
                ...s,
                delayMinutes: Math.max(0, Number(e.target.value) || 0),
              })
            }
          />
        )}
        {s.mode === "at_time" && (
          <YhInput
            label="Send at (HH:MM)"
            value={s.atTime || "10:00"}
            onChange={(e) => onChange({ ...s, atTime: e.target.value })}
            placeholder="09:00"
          />
        )}
        {s.mode === "between" && (
          <>
            <YhInput
              label="Window start (HH:MM)"
              value={s.betweenStart || "09:00"}
              onChange={(e) =>
                onChange({ ...s, betweenStart: e.target.value })
              }
            />
            <YhInput
              label="Window end (HH:MM)"
              value={s.betweenEnd || "20:00"}
              onChange={(e) => onChange({ ...s, betweenEnd: e.target.value })}
            />
          </>
        )}
        {s.mode === "multiple" && (
          <YhInput
            label="Times (comma-separated HH:MM)"
            value={Array.isArray(s.times) ? s.times.join(", ") : "10:00, 16:00"}
            onChange={(e) =>
              onChange({
                ...s,
                times: e.target.value
                  .split(",")
                  .map((t: string) => t.trim())
                  .filter(Boolean),
              })
            }
            placeholder="10:00, 14:00, 18:00"
          />
        )}
        {s.mode !== "immediate" && (
          <p className="text-[10px] text-[#9CA3AF]">
            Timezone: {s.timezone || "Asia/Kolkata"}
          </p>
        )}
      </div>
    );
  };

  const renderStageTriggerFields = () => {
    const data = selectedNode?.data as WorkflowNodeData;
    return (
      <>
        <YhSelect
          label="Trigger when"
          value={data?.triggerOn || "enter_stage"}
          onChange={(e: any) =>
            updateSelectedData({ triggerOn: selectValue(e) || "enter_stage" })
          }
          options={[
            { label: "Lead enters stage", value: "enter_stage" },
            { label: "Lead leaves stage", value: "leave_stage" },
            { label: "Lead status equals stage", value: "equals_stage" },
            { label: "Only via flow connection", value: "flow" },
          ]}
        />
        {(data?.triggerOn || "enter_stage") !== "flow" && (
          <YhSelect
            label="Lead stage / status"
            value={data?.triggerStage || data?.stageKey || ""}
            onChange={(e: any) => {
              const stage = selectValue(e);
              updateSelectedData({
                triggerStage: stage,
                stageKey: stage,
                stageLabel: stage,
              });
            }}
            options={[
              { label: "Select stage", value: "" },
              ...leadStages.map((s) => ({ label: s.label, value: s.id })),
              // Common calling-done aliases if not in master
              { label: "Visit Done", value: "Visit Done" },
              { label: "Call Back", value: "Call Back" },
              { label: "Not Answered", value: "Not Answered" },
            ].filter(
              (opt, idx, arr) =>
                arr.findIndex((x) => x.value === opt.value) === idx
            )}
          />
        )}
        <p className="text-[11px] text-[#9CA3AF]">
          Example: after calling is done, set stage to Visit Done / Call Back —
          this block runs thank-you and other actions automatically.
        </p>
      </>
    );
  };

  const renderMultiActionsEditor = () => {
    const actions = getActions();
    return (
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-[#374151]">Actions</p>
          <button
            type="button"
            className="text-xs font-semibold text-[#1E3A5F] hover:underline"
            onClick={() => addAction("send_whatsapp")}
          >
            + Add action
          </button>
        </div>
        {!actions.length ? (
          <p className="text-xs text-[#9CA3AF]">
            No actions yet. Add WhatsApp, SMS, email, stage update, etc.
          </p>
        ) : null}
        {actions.map((action, index) => (
          <div
            key={action.id || index}
            className="rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] p-3"
          >
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-[#111827]">
                #{index + 1} {action.label || action.type}
              </p>
              <button
                type="button"
                className="text-[11px] font-semibold text-[#B91C1C] hover:underline"
                onClick={() => removeAction(action.id)}
              >
                Remove
              </button>
            </div>
            <div className="flex flex-col gap-2">
              <YhSelect
                label="Action type"
                value={action.type || "send_whatsapp"}
                onChange={(e: any) => {
                  const type = selectValue(e) || "send_whatsapp";
                  updateAction(action.id, {
                    type,
                    label:
                      ACTION_TYPE_OPTIONS.find((o) => o.value === type)
                        ?.label || type,
                  });
                }}
                options={ACTION_TYPE_OPTIONS}
              />
              {["send_whatsapp", "feedback"].includes(action.type) && (
                <>
                  {action.type === "send_whatsapp" && (
                    <YhSelect
                      label="Message purpose"
                      value={action.actionKey || "thank_you"}
                      onChange={(e: any) =>
                        updateAction(action.id, {
                          actionKey: selectValue(e),
                        })
                      }
                      options={WA_PURPOSE_OPTIONS}
                    />
                  )}
                  {action.type === "feedback" &&
                    renderFeedbackFormPicker(
                      action.feedbackFormId || action.formId || "",
                      (formId) =>
                        updateAction(action.id, {
                          feedbackFormId: formId,
                          formId,
                          actionKey: "feedback",
                        })
                    )}
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold text-[#374151]">
                      WhatsApp template
                    </p>
                    <button
                      type="button"
                      className="text-[11px] font-semibold text-[#1E3A5F] hover:underline"
                      onClick={loadWhatsAppTemplates}
                      disabled={loadingTemplates}
                    >
                      {loadingTemplates ? "Loading…" : "Refresh"}
                    </button>
                  </div>
                  <YhSelect
                    label="Template"
                    value={action.templateWid || ""}
                    onChange={(e: any) => {
                      const wid = selectValue(e);
                      const tpl = templates.find((t) => t.wid === wid);
                      updateAction(action.id, {
                        templateWid: wid,
                        templateName: tpl?.name || "",
                      });
                    }}
                    options={[
                      {
                        label: templates.length
                          ? "Select template"
                          : "Configure later",
                        value: "",
                      },
                      ...templates.map((t) => ({
                        label: t.label,
                        value: t.wid,
                      })),
                    ]}
                  />
                  {renderScheduleFields(action.schedule, (schedule) =>
                    updateAction(action.id, { schedule })
                  )}
                </>
              )}
              {action.type === "update_stage" && (
                <YhSelect
                  label="Set lead stage to"
                  value={action.targetStage || ""}
                  onChange={(e: any) =>
                    updateAction(action.id, {
                      targetStage: selectValue(e),
                    })
                  }
                  options={[
                    { label: "Select stage", value: "" },
                    ...leadStages.map((s) => ({
                      label: s.label,
                      value: s.id,
                    })),
                  ]}
                />
              )}
              {action.type === "smart_routing" && (
                <YhSelect
                  label="Routing mode"
                  value={action.routingMode || "round_robin"}
                  onChange={(e: any) =>
                    updateAction(action.id, {
                      routingMode: selectValue(e),
                    })
                  }
                  options={[
                    { label: "Round robin", value: "round_robin" },
                    { label: "Evenly", value: "evenly" },
                  ]}
                />
              )}
              {["send_sms", "send_email"].includes(action.type) && (
                <YhInput
                  label="Message / subject note"
                  value={action.messageNote || ""}
                  onChange={(e) =>
                    updateAction(action.id, { messageNote: e.target.value })
                  }
                  placeholder="Optional note for this action"
                />
              )}
            </div>
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          {ACTION_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className="rounded-md border border-[#E5E7EB] bg-white px-2 py-1 text-[11px] font-medium text-[#374151] hover:bg-[#F3F4F6]"
              onClick={() => addAction(opt.value)}
            >
              + {opt.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const deleteSelection = useCallback(() => {
    if (selectedEdgeId) {
      setEdges((eds) => eds.filter((e) => e.id !== selectedEdgeId));
      setSelectedEdgeId(null);
      return;
    }
    if (!selectedNodeId) return;
    const node = nodes.find((n) => n.id === selectedNodeId);
    const type = (node?.data as WorkflowNodeData)?.type;
    if (type === "start" || type === "end") {
      openErrorNotification("Start and End nodes cannot be deleted");
      return;
    }
    setNodes((nds) => nds.filter((n) => n.id !== selectedNodeId));
    setEdges((eds) =>
      eds.filter(
        (e) => e.source !== selectedNodeId && e.target !== selectedNodeId
      )
    );
    setSelectedNodeId(null);
  }, [
    selectedEdgeId,
    selectedNodeId,
    nodes,
    setEdges,
    setNodes,
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault();
        deleteSelection();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deleteSelection]);

  const save = async (andPublish = false) => {
    if (!activeOrganizationId || activeOrganizationId === "all") {
      openErrorNotification("Select a specific organization before saving.");
      return;
    }
    if (!name.trim()) {
      openErrorNotification("Workflow name is required");
      return;
    }
    const payloadNodes = fromFlowNodes(nodes);
    if (!payloadNodes.some((n) => n.type === "start")) {
      openErrorNotification("Workflow must have a Start node");
      return;
    }
    if (!payloadNodes.some((n) => n.type === "end")) {
      openErrorNotification("Workflow must have an End node");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: name.trim(),
        description,
        triggerType,
        organizationId: activeOrganizationId,
        nodes: payloadNodes,
        edges: fromFlowEdges(edges),
        scope: workflowScope,
        scopeProjectIds,
        scopeFormIds,
        settings: {
          scope: workflowScope,
          isGlobal: workflowScope === "global",
          projectIds: scopeProjectIds,
          formIds: scopeFormIds,
        },
      };
      let id = toIdString(workflowId);
      if (!id) {
        const res = await createAutomationWorkflow(payload);
        id = toIdString(res?.data?._id || res?._id);
        setWorkflowId(id || null);
        setStatus(res?.data?.status || "draft");
        openSuccessNotification("Workflow created");
        if (id) {
          history.replace(
            urls.automationLeadFlowEditPath.replace(":id", id)
          );
        }
      } else {
        const res = await updateAutomationWorkflow(id, payload);
        setStatus(res?.data?.status || status);
        openSuccessNotification("Workflow saved");
      }
      if (andPublish && id) {
        const pub = await publishAutomationWorkflow(id);
        setStatus(pub?.data?.status || "published");
        openSuccessNotification("Workflow published");
      }
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  const renderWhatsAppFields = () => (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-[#374151]">WhatsApp template</p>
        <button
          type="button"
          className="text-xs font-semibold text-[#1E3A5F] hover:underline"
          onClick={loadWhatsAppTemplates}
          disabled={loadingTemplates}
        >
          {loadingTemplates ? "Loading…" : "Refresh"}
        </button>
      </div>
      <YhSelect
        label="Template"
        value={(selectedNode?.data as WorkflowNodeData)?.templateWid || ""}
        onChange={(e: any) => {
          const wid = selectValue(e);
          const tpl = templates.find((t) => t.wid === wid);
          updateSelectedData({
            templateWid: wid,
            templateName: tpl?.name || "",
          });
        }}
        options={[
          {
            label: templates.length ? "Select template" : "Configure later",
            value: "",
          },
          ...templates.map((t) => ({ label: t.label, value: t.wid })),
        ]}
      />
      {(selectedNode?.data as WorkflowNodeData)?.templateName ? (
        <p className="text-xs text-[#6B7280]">
          Selected: {(selectedNode?.data as WorkflowNodeData).templateName}
        </p>
      ) : (
        <p className="text-xs text-[#9CA3AF]">
          Leave empty for now — bind AuthKey template later.
        </p>
      )}
      {renderScheduleFields(
        (selectedNode?.data as WorkflowNodeData)?.schedule,
        (schedule) => updateSelectedData({ schedule })
      )}
    </>
  );

  const grouped = {
    flow: WORKFLOW_BLOCKS.filter((b) => b.category === "flow"),
    source: WORKFLOW_BLOCKS.filter((b) => b.category === "source"),
    action: WORKFLOW_BLOCKS.filter((b) => b.category === "action"),
    message: WORKFLOW_BLOCKS.filter((b) => b.category === "message"),
  };

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-68px)] items-center justify-center bg-white text-sm text-[#6B7280]">
        Loading workflow…
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-[#E5E7EB] bg-white px-4 py-3 sm:px-6">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            className="mb-1 text-xs font-medium text-[#6B7280] hover:text-[#111827]"
            onClick={() => history.push(urls.automationLeadFlowPath)}
          >
            ← Back to workflows
          </button>
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-[240px] max-w-md flex-1">
              <YhInput
                label="Workflow name"
              value={name}
              onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Digital Campaign Process"
                requiredMark
            />
            </div>
            <span className="mt-5 rounded-full bg-[#F3F4F6] px-2.5 py-0.5 text-xs font-semibold capitalize text-[#4B5563]">
              {status}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() => history.push(urls.automationLeadFlowPath)}
          >
            Cancel
          </YhButton>
          <YhButton
            variant="secondary"
            size="sm"
            loading={saving}
            onClick={() => save(false)}
          >
            Save draft
          </YhButton>
          <YhButton size="sm" loading={saving} onClick={() => save(true)}>
            Save & publish
          </YhButton>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[250px_minmax(0,1fr)_300px]">
        <aside className="overflow-y-auto border-r border-[#E5E7EB] bg-white p-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]">
            Blocks
          </p>
          <p className="mb-3 text-[11px] leading-relaxed text-[#9CA3AF]">
            Drag onto the canvas. Drag from a handle to connect. One block can
            connect to many. Select + Delete to remove.
          </p>
          {(
            [
              ["flow", "Flow"],
              ["source", "Sources"],
              ["action", "Actions"],
              ["message", "Messages"],
            ] as const
          ).map(([key, title]) => (
            <div key={key} className="mb-4">
              <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">
                {title}
              </p>
              <div className="flex flex-col gap-1.5">
                {grouped[key].map((b) => (
              <button
                key={b.type}
                type="button"
                    draggable
                    onDragStart={(e) => onPaletteDragStart(e, b.type)}
                onClick={() => addBlock(b.type)}
                    className="flex cursor-grab items-start gap-2.5 rounded-lg border border-[#EEF0F5] bg-white px-2.5 py-2 text-left transition hover:border-[#CBD5E1] hover:bg-[#F8FAFC] active:cursor-grabbing"
                  >
                    <span className="mt-0.5 shrink-0 text-[#1E3A5F]">
                      <BlockIcon type={b.type} className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-semibold text-[#111827]">
                    {b.label}
                  </span>
                      <span className="mt-0.5 block text-[11px] leading-snug text-[#6B7280]">
                  {b.description}
                      </span>
                    </span>
              </button>
            ))}
          </div>
            </div>
          ))}
        </aside>

        <div className="relative min-h-[420px] bg-[#F8FAFC]">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onSelectionChange={onSelectionChange}
            isValidConnection={isValidConnection}
            nodeTypes={nodeTypes}
            connectionMode={ConnectionMode.Loose}
            fitView
            snapToGrid
            snapGrid={[16, 16]}
            deleteKeyCode={null}
            multiSelectionKeyCode="Shift"
            selectionOnDrag={false}
            panOnScroll
            zoomOnScroll
            zoomOnPinch
            minZoom={0.25}
            maxZoom={2}
            defaultEdgeOptions={{
              type: "smoothstep",
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: "#64748B",
                width: 18,
                height: 18,
              },
              style: { stroke: "#94A3B8", strokeWidth: 1.75 },
            }}
            proOptions={{ hideAttribution: true }}
            className="bg-[#F8FAFC]"
          >
            <Background gap={20} size={1} color="#E2E8F0" />
            <Controls
              showInteractive={false}
              className="!overflow-hidden !rounded-lg !border !border-[#E5E7EB] !bg-white !shadow-sm"
            />
            <MiniMap
              nodeStrokeColor="#1E3A5F"
              nodeColor="#E2E8F0"
              maskColor="rgba(15,23,42,0.08)"
              className="!overflow-hidden !rounded-lg !border !border-[#E5E7EB] !bg-white"
            />
          </ReactFlow>

          <div className="pointer-events-none absolute left-3 top-3 z-10 flex gap-2">
            <div className="pointer-events-auto flex overflow-hidden rounded-lg border border-[#E5E7EB] bg-white shadow-sm">
              <button
                type="button"
                className="px-2.5 py-1.5 text-xs font-semibold text-[#374151] hover:bg-[#F8FAFC]"
                onClick={() => zoomOut()}
              >
                −
              </button>
                <button
                  type="button"
                className="border-l border-[#E5E7EB] px-2.5 py-1.5 text-xs font-semibold text-[#374151] hover:bg-[#F8FAFC]"
                onClick={() => zoomIn()}
              >
                +
              </button>
              <button
                type="button"
                className="border-l border-[#E5E7EB] px-2.5 py-1.5 text-xs font-semibold text-[#374151] hover:bg-[#F8FAFC]"
                onClick={() => fitView({ padding: 0.2, duration: 200 })}
              >
                Fit
                </button>
              </div>
            {(selectedNodeId || selectedEdgeId) && (
              <button
                type="button"
                className="pointer-events-auto rounded-lg border border-[#FECACA] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#B91C1C] shadow-sm hover:bg-[#FEF2F2]"
                onClick={deleteSelection}
              >
                {selectedEdgeId ? "Delete connection" : "Delete block"}
              </button>
            )}
          </div>
        </div>

        <aside className="overflow-y-auto border-l border-[#E5E7EB] bg-white p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#9CA3AF]">
            Configure
          </p>
          <div className="mb-4 flex flex-col gap-3">
            <YhInput
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
            />
            <YhSelect
              label="Trigger type"
              value={triggerType}
              onChange={(e: any) =>
                setTriggerType(selectValue(e) || "lead_capture")
              }
              options={[
                { label: "Lead capture", value: "lead_capture" },
                { label: "Stage change", value: "stage_change" },
                { label: "Source", value: "source" },
                { label: "Manual", value: "manual" },
              ]}
            />
            <YhSelect
              label="Workflow scope"
              value={workflowScope}
              onChange={(e: any) =>
                setWorkflowScope(
                  (selectValue(e) || "organization") as
                    | "organization"
                    | "global"
                    | "project"
                    | "form"
                )
              }
              options={[
                { label: "Organization", value: "organization" },
                { label: "Global (fallback)", value: "global" },
                { label: "Project-wise", value: "project" },
                { label: "Form-wise", value: "form" },
              ]}
            />
            {workflowScope === "global" ? (
              <p className="rounded-lg bg-[#F0F9FF] px-3 py-2 text-xs text-[#0369A1]">
                Used when an organization has no published lead-assign workflow.
              </p>
            ) : null}
            {workflowScope === "project" ? (
              <Autocomplete
                multiple
                options={routingOptions.projects || []}
                getOptionLabel={(o: any) =>
                  o?.projectName || o?.name || String(o?._id || "")
                }
                value={(routingOptions.projects || []).filter((p) =>
                  scopeProjectIds.includes(String(p._id || p.projectId))
                )}
                onChange={(_e, value) =>
                  setScopeProjectIds(
                    value.map((v: any) => String(v._id || v.projectId))
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Projects"
                    size="small"
                    placeholder="Select projects"
                  />
                )}
              />
            ) : null}
            {workflowScope === "form" ? (
              <Autocomplete
                multiple
                options={feedbackForms || []}
                getOptionLabel={(o: any) =>
                  o?.form_name || String(o?._id || "")
                }
                value={(feedbackForms || []).filter((f) =>
                  scopeFormIds.includes(String(f._id))
                )}
                onChange={(_e, value) =>
                  setScopeFormIds(value.map((v: any) => String(v._id)))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Forms"
                    size="small"
                    placeholder="Select forms"
                  />
                )}
              />
            ) : null}
          </div>

          {selectedEdge ? (
            <div className="flex flex-col gap-3">
              <p className="text-sm font-semibold text-[#111827]">
                Connection selected
              </p>
              <YhInput
                label="Edge label"
                value={
                  typeof selectedEdge.label === "string"
                    ? selectedEdge.label
                    : ""
                }
                onChange={(e) =>
                  setEdges((eds) =>
                    eds.map((edge) =>
                      edge.id === selectedEdge.id
                        ? { ...edge, label: e.target.value }
                        : edge
                    )
                  )
                }
                placeholder="e.g. reminder / branch"
              />
              <YhButton variant="danger" size="sm" onClick={deleteSelection}>
                Delete connection
              </YhButton>
            </div>
          ) : !selectedNode ? (
            <p className="text-sm text-[#6B7280]">
              Select a block or connection. Drag a handle to another block to
              connect (supports multiple branches).
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2">
                <span className="mt-0.5 text-[#1E3A5F]">
                  <BlockIcon type={selectedType} className="h-5 w-5" />
                </span>
              <div>
                <p className="text-sm font-semibold text-[#111827]">
                    {(selectedNode.data as WorkflowNodeData)?.label ||
                      blockMeta(selectedType).label}
                </p>
                <p className="text-xs text-[#6B7280]">
                    {blockMeta(selectedType).description}
                </p>
                </div>
              </div>
              <YhInput
                label="Block label"
                value={(selectedNode.data as WorkflowNodeData)?.label || ""}
                onChange={(e) => updateSelectedData({ label: e.target.value })}
              />

              {selectedType === "lead_source" && (
                <>
                  {(() => {
                    const SOURCE_GROUP_OPTIONS = [
                      { value: "all", label: "All sources" },
                      { value: "social", label: "Social platform" },
                      { value: "property_portal", label: "Property portal" },
                      { value: "bulk_upload", label: "Bulk upload" },
                      { value: "organic", label: "Organic" },
                      { value: "offline", label: "Offline" },
                      { value: "other", label: "Other" },
                    ];
                    const nodeData = selectedNode.data as WorkflowNodeData;
                    const selectedGroups: string[] = Array.isArray(
                      nodeData?.sourceGroups
                    )
                      ? nodeData.sourceGroups.map(String).filter(Boolean)
                      : nodeData?.matchAllSources
                        ? ["all"]
                        : nodeData?.sourceGroup
                          ? [String(nodeData.sourceGroup)]
                          : ["all"];
                    const allSourcesSelected = selectedGroups.includes("all");
                    const sourceOptions = routingOptions.sources
                      .filter((s) => {
                        if (allSourcesSelected || !selectedGroups.length) {
                          return true;
                        }
                        return selectedGroups.includes(
                          String(s.sourceGroup || "other")
                        );
                      })
                      .map((s) => s.source_name);
                    return (
                      <>
                  <Autocomplete
                    multiple
                    options={SOURCE_GROUP_OPTIONS}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    value={SOURCE_GROUP_OPTIONS.filter((o) =>
                      selectedGroups.includes(o.value)
                    )}
                    onChange={(_e, value) => {
                      const picked = value.map((v) => v.value);
                      const next = picked.includes("all")
                        ? ["all"]
                        : picked.filter((v) => v !== "all");
                      const matchAll = next.includes("all") || next.length === 0;
                      const names = matchAll
                        ? []
                        : routingOptions.sources
                            .filter((s) =>
                              next.includes(String(s.sourceGroup || "other"))
                            )
                            .map((s) => s.source_name);
                      updateSelectedData({
                        sourceGroups: matchAll ? ["all"] : next,
                        sourceGroup: matchAll ? "all" : next[0] || "",
                        matchAllSources: matchAll,
                        sources: names,
                      });
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option.value}
                          size="small"
                          label={option.label}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Source groups"
                        placeholder={
                          selectedGroups.length
                            ? "Add groups"
                            : "All sources"
                        }
                        size="small"
                        helperText={
                          allSourcesSelected
                            ? "Runs for every source"
                            : `${selectedGroups.length} group${
                                selectedGroups.length === 1 ? "" : "s"
                              } selected`
                        }
                      />
                    )}
                  />
                  <Autocomplete
                    multiple
                    options={sourceOptions}
                    value={
                      Array.isArray(
                        (selectedNode.data as WorkflowNodeData)?.sources
                      )
                        ? (selectedNode.data as WorkflowNodeData).sources
                        : []
                    }
                    onChange={(_e, value) =>
                      updateSelectedData({ sources: value })
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          size="small"
                          label={option}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Sources"
                        placeholder="Select org sources"
                        size="small"
                      />
                    )}
                  />
                  <div className="flex items-end gap-2">
                    <div className="min-w-0 flex-1">
                      <YhInput
                        label="Add new org source"
                        value={newSourceName}
                        onChange={(e) => setNewSourceName(e.target.value)}
                        placeholder="e.g. Instagram Ads"
                      />
                    </div>
                    <YhButton
                      variant="secondary"
                      size="sm"
                      onClick={async () => {
                        const name = newSourceName.trim();
                        if (!name) return;
                        if (
                          !activeOrganizationId ||
                          activeOrganizationId === "all"
                        ) {
                          openErrorNotification("Select an organization first");
                          return;
                        }
                        try {
                          await upsertOrgLeadSource(activeOrganizationId, {
                            source_name: name,
                            sourceGroup: allSourcesSelected
                              ? "other"
                              : selectedGroups[0] || "other",
                          });
                          setNewSourceName("");
                          await loadRoutingOptions();
                          const existing =
                            ((selectedNode.data as WorkflowNodeData)
                              ?.sources as string[]) || [];
                          const next = existing.includes(name)
                            ? existing
                            : existing.concat(name);
                          updateSelectedData({ sources: next });
                          openSuccessNotification("Source added");
                        } catch (err: any) {
                          openErrorNotification(
                            err?.message || "Failed to add source"
                          );
                        }
                      }}
                    >
                      Add
                    </YhButton>
                  </div>
                  <p className="text-[11px] text-[#9CA3AF]">
                    Pick one or more source groups, or All sources. All sources
                    runs this step for every lead.
                  </p>
                      </>
                    );
                  })()}
                </>
              )}

              {selectedType === "smart_routing" && (
                <>
                  <YhSelect
                    label="Routing mode"
                    value={
                      (selectedNode.data as WorkflowNodeData)?.routingMode ||
                      "round_robin"
                    }
                    onChange={(e: any) =>
                      updateSelectedData({ routingMode: selectValue(e) })
                    }
                    options={[
                      { label: "Round robin", value: "round_robin" },
                      { label: "Evenly", value: "evenly" },
                    ]}
                  />
                  <YhSelect
                    label="Assign using"
                    value={(() => {
                      const raw = String(
                        (selectedNode.data as WorkflowNodeData)?.assignTarget ||
                          "project"
                      );
                      return raw === "person" ? "member" : raw;
                    })()}
                    onChange={(e: any) =>
                      updateSelectedData({
                        assignTarget: selectValue(e) || "project",
                      })
                    }
                    options={[
                      {
                        label: "Project's assigned team and members",
                        value: "project",
                      },
                      { label: "Department", value: "department" },
                      { label: "Team", value: "team" },
                      { label: "Member", value: "member" },
                    ]}
                  />
                  {(() => {
                    const nodeData = selectedNode.data as WorkflowNodeData;
                    const rawTarget = String(nodeData?.assignTarget || "project");
                    const assignTarget =
                      rawTarget === "person" ? "member" : rawTarget;

                    // Multi departments (migrate legacy single departmentId)
                    const selectedDeptIds: string[] = Array.isArray(
                      nodeData?.departmentIds
                    )
                      ? nodeData.departmentIds.map(String).filter(Boolean)
                      : nodeData?.departmentId
                        ? [String(nodeData.departmentId)]
                        : [];

                    // Multi team types (empty = all). Migrate legacy teamRole.
                    const TEAM_TYPE_OPTIONS = [
                      { value: "sales", label: "Sales team" },
                      { value: "pre_sales", label: "Pre-sales team" },
                    ];
                    const selectedTeamRoles: string[] = Array.isArray(
                      nodeData?.teamRoles
                    )
                      ? nodeData.teamRoles.map(String).filter(Boolean)
                      : nodeData?.teamRole
                        ? [String(nodeData.teamRole)]
                        : [];

                    const selectedTeamIds: string[] = Array.isArray(
                      nodeData?.teamIds
                    )
                      ? nodeData.teamIds.map(String)
                      : [];
                    const selectedUserIds: string[] = Array.isArray(
                      nodeData?.userIds
                    )
                      ? nodeData.userIds.map(String)
                      : [];

                    const filteredTeams = routingOptions.teams.filter((t) => {
                      if (
                        selectedDeptIds.length &&
                        !selectedDeptIds.includes(String(t.departmentId || ""))
                      ) {
                        return false;
                      }
                      if (selectedTeamRoles.length) {
                        const role = String(t.teamRole || "").toLowerCase();
                        const ok = selectedTeamRoles.some((r) => {
                          const n = r.toLowerCase();
                          if (n === "sales")
                            return (
                              !role ||
                              role === "sales" ||
                              role === "sale" ||
                              role.includes("sales")
                            );
                          if (n === "pre_sales")
                            return (
                              role === "pre_sales" ||
                              role === "presales" ||
                              role.includes("pre")
                            );
                          return role === n;
                        });
                        if (!ok && t.teamRole) return false;
                      }
                      return true;
                    });

                    const filteredMembers = routingOptions.members.filter(
                      (m) => {
                        if (!selectedTeamIds.length) {
                          if (!selectedDeptIds.length && !selectedTeamRoles.length)
                            return true;
                          const poolTeamIds = filteredTeams.map((t) => t.teamId);
                          if (!poolTeamIds.length) return true;
                          return (m.teamIds || []).some((tid) =>
                            poolTeamIds.includes(tid)
                          );
                        }
                        return (m.teamIds || []).some((tid) =>
                          selectedTeamIds.includes(tid)
                        );
                      }
                    );

                    const syncDeptFields = (departmentIds: string[]) => {
                      const names = routingOptions.departments
                        .filter((d) => departmentIds.includes(d._id))
                        .map((d) => d.name);
                      const keepTeamIds = selectedTeamIds.filter((tid) =>
                        routingOptions.teams.some(
                          (t) =>
                            t.teamId === tid &&
                            (!departmentIds.length ||
                              departmentIds.includes(
                                String(t.departmentId || "")
                              ))
                        )
                      );
                      updateSelectedData({
                        departmentIds,
                        departmentId: departmentIds[0] || "",
                        departmentName: names[0] || "",
                        teamIds: keepTeamIds,
                      });
                    };

                    return (
                      <>
                        {assignTarget === "project" ? (
                          <p className="text-[11px] leading-relaxed text-[#6B7280]">
                            Each lead is assigned only to the team and members
                            saved on its project. A commercial project stays
                            with the commercial team. If that project has no
                            team, the lead is flagged for manual review and is
                            not sent to a default team.
                          </p>
                        ) : null}
                        {assignTarget === "department" ? (
                        <>
                        <Autocomplete
                          multiple
                          options={routingOptions.departments}
                          getOptionLabel={(o) => o.name || o._id}
                          isOptionEqualToValue={(a, b) => a._id === b._id}
                          value={routingOptions.departments.filter((d) =>
                            selectedDeptIds.includes(d._id)
                          )}
                          onChange={(_e, value) =>
                            syncDeptFields(value.map((v) => v._id))
                          }
                          noOptionsText="No departments found"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Departments"
                              placeholder={
                                selectedDeptIds.length
                                  ? "Add departments"
                                  : "Select departments"
                              }
                              size="small"
                              helperText={
                                selectedDeptIds.length
                                  ? `${selectedDeptIds.length} selected`
                                  : "Leads go only to teams in these departments"
                              }
                            />
                          )}
                        />
                        <div className="mb-2 flex flex-wrap gap-1">
                    <button
                      type="button"
                            className="rounded border border-[#E5E7EB] px-2 py-0.5 text-[11px] text-[#374151] hover:bg-[#F3F4F6]"
                            onClick={() =>
                              syncDeptFields(
                                routingOptions.departments.map((d) => d._id)
                              )
                            }
                          >
                            Select all departments
                          </button>
                          <button
                            type="button"
                            className="rounded border border-[#E5E7EB] px-2 py-0.5 text-[11px] text-[#374151] hover:bg-[#F3F4F6]"
                            onClick={() => syncDeptFields([])}
                          >
                            Clear (all)
                    </button>
                  </div>
                        </>
                        ) : null}

                        {(assignTarget === "department" ||
                          assignTarget === "team") ? (
                        <Autocomplete
                          multiple
                          options={TEAM_TYPE_OPTIONS}
                          getOptionLabel={(o) => o.label}
                          isOptionEqualToValue={(a, b) => a.value === b.value}
                          value={TEAM_TYPE_OPTIONS.filter((o) =>
                            selectedTeamRoles.includes(o.value)
                          )}
                          onChange={(_e, value) => {
                            const teamRoles = value.map((v) => v.value);
                            updateSelectedData({
                              teamRoles,
                              teamRole: teamRoles[0] || "",
                              teamLabel: teamRoles.length
                                ? teamRoles
                                    .map((r) =>
                                      r === "pre_sales"
                                        ? "Pre-Sales"
                                        : "Sales"
                                    )
                                    .join(" + ") + " Team"
                                : "All team types",
                            });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Team types"
                              placeholder={
                                selectedTeamRoles.length
                                  ? "Add types"
                                  : "All team types"
                              }
                              size="small"
                              helperText={
                                selectedTeamRoles.length
                                  ? `${selectedTeamRoles.length} selected`
                                  : "Empty = sales + pre-sales"
                              }
                            />
                          )}
                        />
                        ) : null}

                        {(assignTarget === "department" ||
                          assignTarget === "team" ||
                          assignTarget === "member") && (
                          <>
                            <Autocomplete
                              multiple
                              options={filteredTeams}
                              getOptionLabel={(o) =>
                                o.departmentName
                                  ? `${o.teamName} (${o.departmentName})`
                                  : o.teamName || o.teamId
                              }
                              isOptionEqualToValue={(a, b) =>
                                a.teamId === b.teamId
                              }
                              value={filteredTeams.filter((t) =>
                                selectedTeamIds.includes(t.teamId)
                              )}
                              onChange={(_e, value) => {
                                const nextTeamIds = value.map((v) => v.teamId);
                                const nextUserIds = selectedUserIds.filter(
                                  (uid) =>
                                    routingOptions.members.some(
                                      (m) =>
                                        m.userId === uid &&
                                        (m.teamIds || []).some((tid) =>
                                          nextTeamIds.includes(tid)
                                        )
                                    )
                                );
                                updateSelectedData({
                                  teamIds: nextTeamIds,
                                  userIds:
                                    nextTeamIds.length === 0
                                      ? selectedUserIds
                                      : nextUserIds,
                                });
                              }}
                              noOptionsText={
                                selectedDeptIds.length
                                  ? "No teams in selected departments"
                                  : "No teams linked to this organization"
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Teams"
                                  placeholder={
                                    selectedTeamIds.length
                                      ? "Add teams"
                                      : "All matching teams"
                                  }
                                  size="small"
                                  helperText={
                                    selectedTeamIds.length
                                      ? `${selectedTeamIds.length} selected`
                                      : "Empty = all teams in filters above"
                                  }
                                />
                              )}
                            />
                            <div className="mb-2 flex flex-wrap gap-1">
                              <button
                                type="button"
                                className="rounded border border-[#E5E7EB] px-2 py-0.5 text-[11px] text-[#374151] hover:bg-[#F3F4F6]"
                                onClick={() =>
                                  updateSelectedData({
                                    teamIds: filteredTeams.map((t) => t.teamId),
                                  })
                                }
                              >
                                Select all teams
                              </button>
                              <button
                                type="button"
                                className="rounded border border-[#E5E7EB] px-2 py-0.5 text-[11px] text-[#374151] hover:bg-[#F3F4F6]"
                                onClick={() =>
                                  updateSelectedData({
                                    teamIds: [],
                                    userIds: [],
                                  })
                                }
                              >
                                Clear teams
                              </button>
                            </div>
                          </>
                        )}

                        {assignTarget === "member" ? (
                        <>
                        <Autocomplete
                          multiple
                          options={filteredMembers}
                          getOptionLabel={(o) =>
                            o.email ? `${o.name} (${o.email})` : o.name
                          }
                          isOptionEqualToValue={(a, b) =>
                            a.userId === b.userId
                          }
                          value={filteredMembers.filter((m) =>
                            selectedUserIds.includes(m.userId)
                          )}
                          onChange={(_e, value) =>
                            updateSelectedData({
                              userIds: value.map((v) => v.userId),
                            })
                          }
                          noOptionsText={
                            selectedTeamIds.length
                              ? "No members in selected teams"
                              : "No organization members found"
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Members"
                              placeholder={
                                selectedUserIds.length
                                  ? "Add people"
                                  : "Select people"
                              }
                              size="small"
                              helperText={
                                selectedUserIds.length
                                  ? `${selectedUserIds.length} selected`
                                  : "Select at least one person"
                              }
                            />
                          )}
                        />
                        <div className="mb-2 flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="rounded border border-[#E5E7EB] px-2 py-0.5 text-[11px] text-[#374151] hover:bg-[#F3F4F6]"
                            onClick={() =>
                              updateSelectedData({
                                userIds: filteredMembers.map((m) => m.userId),
                              })
                            }
                          >
                            Select all members
                          </button>
                          <button
                            type="button"
                            className="rounded border border-[#E5E7EB] px-2 py-0.5 text-[11px] text-[#374151] hover:bg-[#F3F4F6]"
                            onClick={() => updateSelectedData({ userIds: [] })}
                          >
                            Clear members
                          </button>
                        </div>
                        </>
                        ) : null}
                        <p className="text-[11px] text-[#9CA3AF]">
                          {assignTarget === "member"
                            ? "Leads are assigned only to the selected people."
                            : assignTarget === "department"
                              ? "Leads go to every team in the selected departments."
                              : assignTarget === "team"
                                ? "Leads go only to the selected teams and their members."
                                : "Leave project filter empty to use this for every project."}
                        </p>
                      </>
                    );
                  })()}

                  <Autocomplete
                    multiple
                    options={routingOptions.projects}
                    getOptionLabel={(o) =>
                      o.location
                        ? `${o.projectName} · ${o.location}`
                        : o.projectName
                    }
                    isOptionEqualToValue={(a, b) => a._id === b._id}
                    value={routingOptions.projects.filter((p) =>
                      (
                        (selectedNode.data as WorkflowNodeData)?.projectIds ||
                        []
                      ).includes(p._id)
                    )}
                    onChange={(_e, value) =>
                      updateSelectedData({
                        projectIds: value.map((v) => v._id),
                      })
                    }
                    noOptionsText="No projects for this organization"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Project filter (optional)"
                        placeholder="Only leads for these projects"
                        size="small"
                      />
                    )}
                  />

                  <Autocomplete
                    multiple
                    options={routingOptions.locations}
                    value={
                      Array.isArray(
                        (selectedNode.data as WorkflowNodeData)?.locations
                      )
                        ? (selectedNode.data as WorkflowNodeData).locations
                        : []
                    }
                    onChange={(_e, value) =>
                      updateSelectedData({ locations: value })
                    }
                    noOptionsText="No locations found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Location filter (optional)"
                        placeholder="Only leads for these locations"
                        size="small"
                      />
                    )}
                  />
                  <p className="text-[11px] text-[#9CA3AF]">
                    When project/location filters are set, only matching leads
                    are assigned to the selected team or person.
                  </p>
                </>
              )}

              {selectedType === "masked_call" && (
                  <YhSelect
                  label="Number masking"
                  value={
                    (selectedNode.data as WorkflowNodeData)?.maskingEnabled ===
                    false
                      ? "off"
                      : "on"
                  }
                  onChange={(e: any) =>
                    updateSelectedData({
                      maskingEnabled: selectValue(e) !== "off",
                    })
                  }
                  options={[
                    { label: "Enabled", value: "on" },
                    { label: "Disabled", value: "off" },
                  ]}
                />
              )}

              {selectedType === "send_whatsapp" && (
                <>
                  {renderStageTriggerFields()}
                  <YhSelect
                    label="Message purpose"
                    value={
                      (selectedNode.data as WorkflowNodeData)?.actionKey ||
                      "thank_you"
                    }
                    onChange={(e: any) =>
                      updateSelectedData({ actionKey: selectValue(e) })
                    }
                    options={WA_PURPOSE_OPTIONS}
                  />
                  {renderWhatsAppFields()}
                </>
              )}

              {selectedType === "feedback" && (
                <>
                  {renderStageTriggerFields()}
                  {renderFeedbackFormPicker(
                    (selectedNode.data as WorkflowNodeData)?.feedbackFormId ||
                      (selectedNode.data as WorkflowNodeData)?.formId ||
                      "",
                    (formId) =>
                      updateSelectedData({
                        feedbackFormId: formId,
                        formId,
                        actionKey: "feedback",
                      })
                  )}
                  {renderWhatsAppFields()}
                </>
              )}

              {(selectedType === "stage_action" ||
                selectedType === "multi_action") && (
                <>
                  {renderStageTriggerFields()}
                  {selectedType === "stage_action" && (
                    <YhSelect
                      label="Also set stage label on node"
                      value={
                        (selectedNode.data as WorkflowNodeData)?.stageLabel ||
                        (selectedNode.data as WorkflowNodeData)?.triggerStage ||
                        ""
                      }
                    onChange={(e: any) => {
                        const stage = selectValue(e);
                      updateSelectedData({
                          stageLabel: stage,
                          stageKey: stage,
                          triggerStage:
                            (selectedNode.data as WorkflowNodeData)
                              ?.triggerStage || stage,
                      });
                    }}
                    options={[
                        { label: "Select stage", value: "" },
                        ...leadStages.map((s) => ({
                          label: s.label,
                          value: s.id,
                      })),
                    ]}
                  />
                  )}
                  {renderMultiActionsEditor()}
                </>
              )}

              {selectedType === "follow_up" && (
                <YhSelect
                  label="Follow-up owner"
                  value={
                    (selectedNode.data as WorkflowNodeData)?.followUpType ||
                    "sales"
                  }
                  onChange={(e: any) =>
                    updateSelectedData({ followUpType: selectValue(e) })
                  }
                  options={[
                    { label: "Sales", value: "sales" },
                    { label: "Pre-sales", value: "pre_sales" },
                  ]}
                />
              )}

              {selectedType === "condition" && (
                <YhInput
                  label="Condition expression"
                  value={
                    (selectedNode.data as WorkflowNodeData)?.expression || ""
                  }
                  onChange={(e) =>
                    updateSelectedData({ expression: e.target.value })
                  }
                  placeholder="e.g. source == facebook"
                />
              )}

              {selectedType === "opportunity" && (
                <p className="text-xs text-[#6B7280]">
                  Terminal sales outcome node (Won / Lost). Connect this to End.
                </p>
              )}

              {selectedType !== "start" && selectedType !== "end" ? (
                <YhButton variant="danger" size="sm" onClick={deleteSelection}>
                  Delete block
              </YhButton>
              ) : (
                <p className="text-xs text-[#9CA3AF]">
                  Start and End are required endpoints.
                </p>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

const AutomationEditor: React.FC = () => (
  <ReactFlowProvider>
    <AutomationEditorInner />
  </ReactFlowProvider>
);

export default AutomationEditor;
